import Crc32 from 'crc-32';
import UAParser from 'ua-parser-js';

import {
  ABTestRules,
  ABTestRulesNames,
  ABTestScope,
  ABTestService,
  BrowserType,
  ExperimentName,
  ExperimentVariantName,
  ExperimentVariantNames,
} from './ab-test.types.js';

export function lerp(min: number, max: number, value: number): number {
  return min * (1 - value) + max * value;
}

export class DefaultABTestService<R extends ABTestRules> implements ABTestService<R> {
  // Caches the user variants. RUID -> Experiment -> Variant
  private cache = new Map<string, Map<ABTestRulesNames<R>, ExperimentVariantName>>();

  constructor(
    private rules: R,
    private overrides: Partial<Record<ABTestRulesNames<R>, ExperimentVariantName>>,
    private enableCache: boolean,
  ) {}

  addOverrides(newOverrides: Partial<Record<ABTestRulesNames<R>, ExperimentVariantName>>): void {
    this.overrides = {
      ...this.overrides,
      ...newOverrides,
    };
  }

  getUserVariant<N extends ABTestRulesNames<R>>(
    ruid: string,
    experiment: N,
    userAgent: string | null,
    scope?: ABTestScope<N>,
  ): ExperimentVariantNames<R[N]> {
    // Check for scope overrides
    const scopeOverride = scope?.experiments?.[experiment];
    if (scopeOverride != null) {
      return scopeOverride;
    }

    // Check for local overrides
    const localOverride = this.overrides[experiment];
    if (localOverride != null) {
      return localOverride;
    }

    // Check for cache hits
    if (this.enableCache) {
      const cachedUserExperiments = this.cache.get(ruid);
      if (cachedUserExperiments != null) {
        const cachedExperimentVariant = cachedUserExperiments.get(experiment);
        if (cachedExperimentVariant != null) {
          return cachedExperimentVariant;
        }
      }
    }

    const rule = this.rules[experiment];
    if (rule == null) {
      throw new Error(`Rule for experiment ${experiment} not found`);
    }

    // Validate if we have everything that we need to properly compute
    // the experiment. If the rule was a condition we NEED to have the user agent.
    // Otherwise,  we need to throw to avoid discrepancies between the backend and frontend.
    if (userAgent == null) {
      for (const w of Object.values(rule.variants).flat()) {
        if (w.conditions == null) {
          continue;
        }
        if (w.conditions.mobile == null && w.conditions.browser == null) {
          continue;
        }
        throw new Error(`User agent not provided but needed to compute experiment ${experiment}`);
      }
    }

    let mobile: boolean | null = null;
    let browser: BrowserType | null = null;

    if (userAgent != null) {
      const uaParser = new UAParser(userAgent);

      const device = uaParser.getDevice();
      if (device.type != null && ['mobile', 'tablet'].includes(device.type)) {
        mobile = true;
      }

      const uaBrowser = uaParser.getBrowser();
      if (uaBrowser.name != null) {
        if (uaBrowser.name.toLowerCase().includes('safari')) {
          browser = 'safari';
        } else if (uaBrowser.name.toLowerCase().includes('chrome')) {
          browser = 'chrome';
        } else if (uaBrowser.name.toLowerCase().includes('firefox')) {
          browser = 'firefox';
        }
      }
    }

    // We need to find the first we find that matches this device.
    const eligibleVariants = Object.entries(rule.variants).map(([variantName, weights]) => {
      let weight = 0;

      for (const w of weights) {
        if (w.conditions == null || (w.conditions.mobile == null && w.conditions.browser == null)) {
          weight = w.weight;
          break;
        }
        let matchesMobile = w.conditions.mobile == null;
        if (w.conditions.mobile != null && mobile != null) {
          matchesMobile = w.conditions.mobile === mobile;
        }
        let matchesBrowser = w.conditions.browser == null;
        if (w.conditions.browser != null && browser != null) {
          matchesBrowser = w.conditions.browser.includes(browser);
        }

        if (matchesMobile && matchesBrowser) {
          weight = w.weight;
          break;
        }
      }

      return {
        variant: variantName,
        weight,
      };
    });

    // Get the user crc32 hash and convert it to a value between 0 and 1
    const hashValue = Crc32.str(ruid + experiment) >>> 0;
    const normalizedHashValue = lerp(0, 1, hashValue / (2 ** 32 - 1));

    // Find the variant
    const cumulativeWeights = eligibleVariants.reduce((acc, variant, index) => {
      const weight = variant.weight;
      const cumulativeWeight = index === 0 ? weight : acc[index - 1] + weight;
      acc.push(cumulativeWeight);
      return acc;
    }, [] as number[]);

    const selectedVariant = eligibleVariants.find((_, index) => {
      const cumulativeWeight = cumulativeWeights[index];
      return normalizedHashValue <= cumulativeWeight;
    });
    const userVariant = selectedVariant?.variant ?? rule.defaultVariant;

    // Cache the variant
    if (this.enableCache) {
      const cachedUserExperiments = this.cache.get(ruid) ?? new Map<ABTestRulesNames<R>, ExperimentVariantName>();
      if (cachedUserExperiments.size == 0) {
        this.cache.set(ruid, cachedUserExperiments);
      }
      cachedUserExperiments.set(experiment, userVariant);
    }

    return userVariant;
  }

  getAllUserVariants(
    ruid: string,
    userAgent: string | null,
    scope?: ABTestScope<ABTestRulesNames<R>>,
  ): Record<ABTestRulesNames<R>, ExperimentVariantName> {
    return Object.keys(this.rules)
      .map((e) => e as ABTestRulesNames<R>)
      .reduce(
        (acc, experiment) => {
          acc[experiment] = this.getUserVariant(ruid, experiment, userAgent, scope) as ExperimentVariantName;
          return acc;
        },
        {} as Record<ABTestRulesNames<R>, ExperimentVariantName>,
      );
  }
}
